/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { Options } from "react-select";
import { ISite } from "./site";
import { TransferSyntaxResponse } from "./transferSyntax";
import { ApplicationEntity, RemoteAEResponse } from "./ae";
import {
  ApplicationEntityType,
  CMoveMode,
  StorageClass,
  UploadMode,
} from "../constant";
import { DropDownResponse, OptionTypeBase } from "./common";
import { IGateways } from "./gateway";
import { IPaginationBaseState } from "./components/pagination";
import { IOrganisation } from "./organisation";
import { SelectChangeFunction } from "./components/reactSelectSearchProps";

export interface LocalAESearch extends IPaginationBaseState {
  searchKeyword?: string;
  page: number;
  status?: boolean;
  gateways: OptionTypeBase[];
  sites: OptionTypeBase[];
  organisations: Array<string>;
  type?: ApplicationEntityType;
}

export interface LocalAE extends ApplicationEntity {
  type: ApplicationEntityType;
  id?: string;
  description: string;
  enabled: boolean;
  gateway?: IGateways;
  organisation?: IOrganisation;
  site?: ISite;
  preferredTransferSyntaxes?: TransferSyntaxResponse[];
  acceptedCallingAEs?: RemoteAEResponse[];
  mode?: string;
  storageClass: string;
  uploadMode: string;
  cmoveMode: string;
  allowProprietarySOPClasses: boolean;
  dicomTls: boolean;
  dicomTlsConfirm?: boolean;
  dataSetId?: string;
  dataSet?: DropDownResponse<string>;
  codeTable?: OptionTypeBase;
  studyValidationBehavior?: string;
}

export const defaultValue: Readonly<LocalAE> = {
  id: "",
  title: "",
  description: "",
  enabled: true,
  preferredTransferSyntaxes: [],
  acceptedCallingAEs: [],
  storageClass: StorageClass.ONLINE,
  uploadMode: UploadMode.ASYNCHRONOUS,
  cmoveMode: CMoveMode.ASYNCHRONOUS,
  type: ApplicationEntityType.GATEWAY as ApplicationEntityType,
  allowProprietarySOPClasses: false,
  dicomTls: false,
};

export interface LocalAEFormProps {
  defaultValue: LocalAE;
  readOnly?: boolean;
  onSubmit?: (formValue: LocalAE) => void;
  typeReadOnly?: boolean;
}

export type GroupedLocalAE = {
  id: string;
  label: string;
  options: LocalAE[];
};

// Group by gateway
export type LocalAEFilterSelectProps = {
  handleSelectChange: SelectChangeFunction;
  placeholder: string;
  name: string;
  isDisabled?: boolean;
  isMulti: boolean;
  defaultValue?: OptionTypeBase | Options<OptionTypeBase> | null;
  value?: OptionTypeBase | Options<OptionTypeBase> | null;
  gateways?: string[];
  isFilter?: boolean;
  type?: ApplicationEntityType;
  localAEs?: LocalAE[];
  isDark?: boolean;
  className?: string;
  fetchData?: () => void;
  handleFocus?: () => void;
  handleBlur?: () => void;
};

// Group by gateway
export type LocalAESelectProps = {
  handleSelectChange: SelectChangeFunction;
  placeholder: string;
  name: string;
  isDisabled?: boolean;
  isMulti: boolean;
  defaultValue?: OptionTypeBase | Options<OptionTypeBase> | null;
  value?: OptionTypeBase | Options<OptionTypeBase> | null;
  gateways?: string[];
  isFilter?: boolean;
  type?: ApplicationEntityType;
  localAEs?: LocalAE[];
  isDark?: boolean;
  className?: string;
  fetchData?: () => void;
  handleFocus?: () => void;
  handleBlur?: () => void;
  OptionValueParams?: Array<LocalAE> | null;
};

export type RemoteAESelectProps = {
  handleSelectChange: SelectChangeFunction;
  placeholder: string;
  name: string;
  isMulti: boolean;
  isDisabled?: boolean;
  defaultValue?: OptionTypeBase | null;
  value?: OptionTypeBase | Options<OptionTypeBase> | null;
  isFilter?: boolean;
  hasAny?: boolean;
  isDark?: boolean;
  type?: ApplicationEntityType;
  remoteAEs?: RemoteAEResponse[];
  fetchData?: () => void;
  handleFocus?: () => void;
  handleBlur?: () => void;
};

export interface LocalApplicationEntityLightResponse {
  id: string;
  title: string;
  port: string;
}

export interface LocalParams {
  mode: string;
  type: string;
}
